import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Download, FileText, Search, X, Trash2, FileAudio, BookOpen, Award, Shield, Settings, Wrench } from 'lucide-react';
import * as resourcesService from '../services/resourcesService';

interface Resource {
  id?: string;
  _id?: string;
  label: string;
  url: string;
  type: 'manual' | 'brochure' | 'catalog' | 'datasheet' | 'certificate' | 'warranty' | 'installation_guide' | 'maintenance_guide' | 'safety_guide';
  productSlug?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

const dummyResources: Resource[] = [
  {
    id: '1',
    label: 'Caterpillar 320D Teknik Broşür',
    url: 'https://example.com/documents/cat-320d-brochure.pdf',
    type: 'brochure',
    productSlug: 'caterpillar-320d-ekskavatoru',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2', 
    label: 'Hitachi ZX200 Kullanım Kılavuzu',
    url: 'https://example.com/documents/hitachi-zx200-manual.pdf',
    type: 'manual',
    productSlug: 'hitachi-zx200-ekskavatoru',
    createdAt: '2024-01-12T14:20:00Z'
  },
  {
    id: '3',
    label: 'İnşaat Sahası Güvenlik Rehberi',
    url: 'https://example.com/documents/safety-guide.pdf',
    type: 'safety_guide',
    productSlug: null,
    createdAt: '2024-01-10T09:15:00Z'
  }
];

const Resources: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFileType, setSelectedFileType] = useState<string>('all');
  const [submitting, setSubmitting] = useState(false);

  // Resources'ları API'den yükle
  const fetchResources = async () => {
    try {
      setLoading(true);
      const resourcesData = await resourcesService.getResources();
      console.log('Resources received:', resourcesData);
      setResources(resourcesData);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Kaynaklar yüklenirken hata oluştu');
      setResources(dummyResources); // Fallback to dummy data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setShowForm(true);
  };

  const handleNewResource = () => {
    setEditingResource(null);
    setShowForm(true);
  };

  const handleDelete = async (resource: Resource) => {
    if (window.confirm('Bu kaynağı silmek istediğinizden emin misiniz?')) {
      try {
        const resourceId = resource.id || resource._id;
        if (!resourceId) {
          toast.error('Kaynak ID bulunamadı');
          return;
        }
        await resourcesService.deleteResource(resourceId);
        toast.success('Kaynak başarıyla silindi');
        fetchResources();
      } catch (error) {
        console.error('Error deleting resource:', error);
        toast.error('Kaynak silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      const resourceData = {
        label: formData.get('label') as string,
        url: formData.get('url') as string,
        type: formData.get('type') as string,
        productSlug: formData.get('productSlug') as string || null
      };

      if (editingResource) {
        const resourceId = editingResource.id || editingResource._id;
        if (!resourceId) {
          toast.error('Kaynak ID bulunamadı');
          return;
        }
        await resourcesService.updateResource(resourceId, resourceData);
        toast.success('Kaynak başarıyla güncellendi');
      } else {
        await resourcesService.createResource(resourceData);
        toast.success('Kaynak başarıyla oluşturuldu');
      }

      setShowForm(false);
      setEditingResource(null);
      fetchResources();
    } catch (error: any) {
      console.error('Error saving resource:', error);
      toast.error('Kaynak kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getFileTypeIcon = (type: string) => {
    const iconClass = "h-5 w-5 mr-2";
    switch (type) {
      case 'manual':
        return <BookOpen className={`${iconClass} text-blue-600`} />;
      case 'brochure':
        return <FileText className={`${iconClass} text-green-600`} />;
      case 'catalog':
        return <BookOpen className={`${iconClass} text-purple-600`} />;
      case 'datasheet':
        return <FileText className={`${iconClass} text-orange-600`} />;
      case 'certificate':
        return <Award className={`${iconClass} text-yellow-600`} />;
      case 'warranty':
        return <Shield className={`${iconClass} text-green-600`} />;
      case 'installation_guide':
        return <Settings className={`${iconClass} text-blue-600`} />;
      case 'maintenance_guide':
        return <Wrench className={`${iconClass} text-gray-600`} />;
      case 'safety_guide':
        return <Shield className={`${iconClass} text-red-600`} />;
      default:
        return <FileText className={`${iconClass} text-gray-600`} />;
    }
  };

  const getFileTypeLabel = (type: string) => {
    switch (type) {
      case 'manual':
        return 'Kullanım Kılavuzu';
      case 'brochure':
        return 'Broşür';
      case 'catalog':
        return 'Katalog';
      case 'datasheet':
        return 'Veri Sayfası';
      case 'certificate':
        return 'Sertifika';
      case 'warranty':
        return 'Garanti';
      case 'installation_guide':
        return 'Kurulum Rehberi';
      case 'maintenance_guide':
        return 'Bakım Rehberi';
      case 'safety_guide':
        return 'Güvenlik Rehberi';
      default:
        return 'Doküman';
    }
  };

  const getFileTypeBadge = (type: string) => {
    const baseClasses = "ml-2 px-2 py-1 text-xs font-medium rounded-full";
    switch (type) {
      case 'manual':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'brochure':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'catalog':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'datasheet':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'certificate':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'warranty':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'installation_guide':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'maintenance_guide':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'safety_guide':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFileType = selectedFileType === 'all' || resource.type === selectedFileType;
    return matchesSearch && matchesFileType;
  });

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kaynak Yönetimi</h1>
            <p className="text-gray-600 mt-2">
              Doküman ve dosya kaynaklarını yönetin
            </p>
          </div>
          <button
            onClick={handleNewResource}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Yeni Kaynak
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Kaynak</p>
                <p className="text-2xl font-bold text-gray-900">{resources.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Download className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Doküman</p>
                <p className="text-2xl font-bold text-gray-900">
                  {resources.filter(r => ['manual', 'brochure', 'datasheet'].includes(r.type)).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Award className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sertifika</p>
                <p className="text-2xl font-bold text-gray-900">
                  {resources.filter(r => r.type === 'certificate').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Kaynaklarda ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="sm:w-48">
                <select
                  value={selectedFileType}
                  onChange={(e) => setSelectedFileType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">Tüm Dosya Türleri</option>
                  <option value="manual">Kullanım Kılavuzu</option>
                  <option value="brochure">Broşür</option>
                  <option value="catalog">Katalog</option>
                  <option value="datasheet">Veri Sayfası</option>
                  <option value="certificate">Sertifika</option>
                  <option value="warranty">Garanti</option>
                  <option value="installation_guide">Kurulum Rehberi</option>
                  <option value="maintenance_guide">Bakım Rehberi</option>
                  <option value="safety_guide">Güvenlik Rehberi</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Kaynak Listesi</h2>
          </div>
          
          <div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tür
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doküman
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredResources.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {searchTerm || selectedFileType !== 'all' ? 'Arama kriterlerine uygun kaynak bulunamadı.' : 'Henüz kaynak bulunmuyor.'}
                    </td>
                  </tr>
                ) : filteredResources.map((resource) => (
                  <tr key={resource._id || resource.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getFileTypeIcon(resource.type)}
                        <span className={getFileTypeBadge(resource.type)}>
                          {getFileTypeLabel(resource.type)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 max-w-sm truncate">
                        {resource.label}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {resource.url.length > 40 ? resource.url.substring(0, 40) + '...' : resource.url}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {resource.createdAt ? new Date(resource.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900"
                          title="İndir"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => handleEdit(resource)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(resource)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Resource Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <FileText className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingResource ? 'Kaynak Düzenle' : 'Yeni Kaynak'}
                </h2>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="space-y-6"
              >
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık
                  </label>
                  <input
                    type="text"
                    name="label"
                    defaultValue={editingResource?.label || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Kaynak başlığını girin"
                    required
                  />
                </div>

                {/* URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL
                  </label>
                  <input
                    type="url"
                    name="url"
                    defaultValue={editingResource?.url || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="https://example.com/document.pdf"
                    required
                  />
                </div>

                {/* File Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dosya Türü
                  </label>
                  <select
                    name="type"
                    defaultValue={editingResource?.type || 'manual'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="manual">Kullanım Kılavuzu</option>
                    <option value="brochure">Broşür</option>
                    <option value="catalog">Katalog</option>
                    <option value="datasheet">Veri Sayfası</option>
                    <option value="certificate">Sertifika</option>
                    <option value="warranty">Garanti</option>
                    <option value="installation_guide">Kurulum Rehberi</option>
                    <option value="maintenance_guide">Bakım Rehberi</option>
                    <option value="safety_guide">Güvenlik Rehberi</option>
                  </select>
                </div>

                {/* Product Slug (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Slug (İsteğe Bağlı)
                  </label>
                  <input
                    type="text"
                    name="productSlug"
                    defaultValue={editingResource?.productSlug || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="caterpillar-320d-ekskavatoru"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Bu kaynak belirli bir ürüne aitse ürün slug'ını girin
                  </p>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingResource ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Resources;